import { Component, lazy, Suspense, useEffect, type ComponentType, type ErrorInfo, type LazyExoticComponent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { ButtonLink, ErrorState, ForbiddenState, Skeleton, SkeletonText } from '@/ui';
import { useSession } from '../AppServicesContext';
import { canAccess, sectionNamespaces, type SectionDefinition } from '../registry';
import { SectionTabs } from '../shell/SectionTabs';
import s from '../layouts/Layout.module.css';

const lazyCache = new Map<string, LazyExoticComponent<ComponentType>>();

function lazySection(section: SectionDefinition): LazyExoticComponent<ComponentType> {
  let component = lazyCache.get(section.id);
  if (!component) {
    component = lazy(section.load);
    lazyCache.set(section.id, component);
  }
  return component;
}

/** Заголовок вкладки браузера: «Раздел · dodoc.kz». */
export function useDocumentTitle(title: string | null): void {
  useEffect(() => {
    document.title = title ? `${title} · dodoc.kz` : 'dodoc.kz';
  }, [title]);
}

export function PageSkeleton() {
  const { t } = useTranslation('shell');
  return (
    <div aria-busy="true" aria-label={t('page.loading')}>
      <Skeleton width="40%" height={32} className={s.skeletonTitle} />
      <SkeletonText lines={5} />
    </div>
  );
}

interface BoundaryProps {
  children: ReactNode;
  fallback: (reset: () => void) => ReactNode;
  resetKey: string;
}

class SectionErrorBoundary extends Component<BoundaryProps, { error: Error | null; key: string }> {
  override state = { error: null as Error | null, key: this.props.resetKey };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  static getDerivedStateFromProps(props: BoundaryProps, state: { error: Error | null; key: string }) {
    return props.resetKey !== state.key ? { error: null, key: props.resetKey } : null;
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Section render failed', error.name, info.componentStack?.split('\n')[1]?.trim());
  }

  override render() {
    if (this.state.error) return this.props.fallback(() => this.setState({ error: null }));
    return this.props.children;
  }
}

function SectionCrash({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('shell');
  return (
    <ErrorState
      title={t('page.crashTitle')}
      description={t('page.crashDescription')}
      onRetry={() => {
        onRetry();
        window.location.reload();
      }}
    />
  );
}

/** Экран раздела: проверка ролей, заголовок вкладки, ленивая загрузка, изоляция ошибок. */
export function SectionPage({ section, publicAccess = false }: { section: SectionDefinition; publicAccess?: boolean }) {
  const { t, ready } = useTranslation(['shell', ...sectionNamespaces(section)]);
  const { state, roles } = useSession();
  const location = useLocation();
  useDocumentTitle(ready ? t(section.titleKey) : null);
  const effectiveRoles = section.area === 'backoffice' ? (state.user?.globalRoles ?? []) : [...roles, ...(state.user?.globalRoles ?? [])];
  if (!publicAccess && section.area !== 'public' && !canAccess(section, effectiveRoles)) {
    return (
      <div className={s.fullPageStatus}>
        <ForbiddenState
          actions={
            <ButtonLink to="/" variant="tertiary">
              {t('shell:page.home')}
            </ButtonLink>
          }
        />
      </div>
    );
  }
  if (!ready) return <PageSkeleton />;
  const Screen = lazySection(section);
  return (
    <>
      {section.tabGroup ? <SectionTabs section={section} roles={effectiveRoles} /> : null}
      <SectionErrorBoundary resetKey={location.pathname} fallback={(reset) => <SectionCrash onRetry={reset} />}>
        <Suspense fallback={<PageSkeleton />}>
          <Screen />
        </Suspense>
      </SectionErrorBoundary>
    </>
  );
}
